import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Rectangle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import SelectionDrawer from '../components/SelectionDrawer';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';

// Utility function to add timeout to fetch requests
const fetchWithTimeout = (url, options = {}, timeout = 10000) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    )
  ]);
};

const INITIAL_MAP_CENTER = [8.9849, -79.5188];

const initialFormState = {
  min_price: '',
  max_price: '',
  property_type: '',
  property_category: '',
  notify_on_new_properties: true,
  notify_on_price_changes: false,
};

const computeBoundsCenter = (bounds) => {
  const { southWest, northEast } = extractCorners(bounds);
  if (!southWest || !northEast) {
    return null;
  }

  return {
    lat: (southWest.lat + northEast.lat) / 2,
    lng: (southWest.lng + northEast.lng) / 2,
  };
};

const extractCorners = (bounds) => {
  if (!bounds) {
    return { southWest: null, northEast: null };
  }

  if (Array.isArray(bounds)) {
    if (bounds.length >= 2 && Array.isArray(bounds[0]) && Array.isArray(bounds[1])) {
      const [sw, ne] = bounds;
      return {
        southWest: { lat: Number(sw[0]), lng: Number(sw[1]) },
        northEast: { lat: Number(ne[0]), lng: Number(ne[1]) },
      };
    }
    return { southWest: null, northEast: null };
  }

  if (typeof bounds === 'object') {
    const sw = bounds.southWest || bounds.south_west || bounds.southwest || null;
    const ne = bounds.northEast || bounds.north_east || bounds.northeast || null;

    const resolvePoint = (point) => {
      if (!point) return null;
      if (Array.isArray(point)) {
        return { lat: Number(point[0]), lng: Number(point[1]) };
      }
      if (typeof point === 'object') {
        const lat = Number(point.lat ?? point.latitude);
        const lng = Number(point.lng ?? point.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
      return null;
    };

    return {
      southWest: resolvePoint(sw),
      northEast: resolvePoint(ne),
    };
  }

  return { southWest: null, northEast: null };
};

const formatBoundsSummary = (bounds) => {
  const { southWest, northEast } = extractCorners(bounds);
  if (!southWest || !northEast) {
    return '';
  }

  const formatPoint = ({ lat, lng }) => {
    const format = (value) => (Number.isFinite(value) ? value.toFixed(3) : '—');
    return `${format(lat)}°, ${format(lng)}°`;
  };

  return `SW ${formatPoint(southWest)} • NE ${formatPoint(northEast)}`;
};

const formatFallbackLabel = (t, center) => {
  if (!center) return t('preferencesPage.selectedArea');
  return t('preferencesPage.areaNear', { lat: center.lat.toFixed(3), lng: center.lng.toFixed(3) });
};

/** Fit map bounds to show all preference areas (used in overview map). */
const FitBoundsToPreferenceAreas = ({ preferences }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !preferences || preferences.length === 0) return;
    const withBounds = preferences.filter((p) => p.selection_bounds && Array.isArray(p.selection_bounds) && p.selection_bounds.length >= 2);
    if (withBounds.length === 0) return;
    const b = new L.LatLngBounds();
    withBounds.forEach((p) => {
      const [sw, ne] = p.selection_bounds;
      if (Array.isArray(sw) && Array.isArray(ne)) b.extend([sw, ne]);
    });
    if (b.isValid()) map.fitBounds(b, { padding: [24, 24], maxZoom: 14 });
  }, [map, preferences]);
  return null;
};

/**
 * While active, Leaflet does not capture one-finger pan/zoom so rectangle drawing can receive the gesture.
 * Restores previous handler states on deactivate/unmount.
 */
const MapSelectionGestureLock = ({ active }) => {
  const map = useMap();
  useLayoutEffect(() => {
    if (!active || !map) return undefined;

    const dragging = map.dragging;
    const touchZoom = map.touchZoom;
    const doubleClickZoom = map.doubleClickZoom;
    const boxZoom = map.boxZoom;
    const keyboard = map.keyboard;

    const prev = {
      dragging: dragging?.enabled?.() ?? true,
      touchZoom: touchZoom?.enabled?.() ?? true,
      doubleClickZoom: doubleClickZoom?.enabled?.() ?? true,
      boxZoom: boxZoom?.enabled?.() ?? true,
      keyboard: keyboard?.enabled?.() ?? true,
    };

    dragging?.disable?.();
    touchZoom?.disable?.();
    doubleClickZoom?.disable?.();
    boxZoom?.disable?.();
    keyboard?.disable?.();

    return () => {
      if (prev.dragging) dragging?.enable?.();
      if (prev.touchZoom) touchZoom?.enable?.();
      if (prev.doubleClickZoom) doubleClickZoom?.enable?.();
      if (prev.boxZoom) boxZoom?.enable?.();
      if (prev.keyboard) keyboard?.enable?.();
    };
  }, [active, map]);

  return null;
};

const AreaPreferencesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { currentLanguage, languages, changeLanguage } = useLanguage();
  const hasSubscription = !!user?.has_active_subscription;
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [selectedBounds, setSelectedBounds] = useState(null);
  const [selectionCenter, setSelectionCenter] = useState(null);
  const [selectionLabel, setSelectionLabel] = useState('');
  const [selectionMeta, setSelectionMeta] = useState(null);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState(INITIAL_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState(11);
  const [mapKey, setMapKey] = useState(0);
  /** On touch / installed PWA, selection drawing is off until the user enables it (same idea as PropertySearchPage). */
  const [mapSelectionActive, setMapSelectionActive] = useState(true);
  const [needsMapSelectionActivation, setNeedsMapSelectionActivation] = useState(false);

  const fetchPreferences = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl('/area-preferences'), {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }, 10000); // 10 second timeout

      const data = await response.json();

      if (data.success) {
        const normalised = (data.data || []).map((preference) => {
          const { southWest, northEast } = extractCorners(
            preference.selection_bounds
              ?? (preference.sw_lat !== null && preference.sw_lng !== null && preference.ne_lat !== null && preference.ne_lng !== null
                ? [
                    [parseFloat(preference.sw_lat), parseFloat(preference.sw_lng)],
                    [parseFloat(preference.ne_lat), parseFloat(preference.ne_lng)],
                  ]
                : null)
          );

          const bounds = southWest && northEast
            ? [
                [southWest.lat, southWest.lng],
                [northEast.lat, northEast.lng],
              ]
            : null;

          return {
            ...preference,
            selection_bounds: bounds,
            selection_label: preference.selection_label ?? preference.region ?? null,
          };
        });

        setPreferences(normalised);
      } else {
        toast.error(t('preferencesPage.failedToLoad'));
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      if (error.message === 'Request timeout') {
        toast.error(t('common.requestTimeout'));
      } else {
        toast.error(t('preferencesPage.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'land_seeker') {
      navigate('/login', { state: { role: 'land_seeker' } });
      return;
    }

    fetchPreferences();
  }, [isAuthenticated, user, navigate, fetchPreferences]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      setNeedsMapSelectionActivation(coarse);
      if (coarse) {
        setMapSelectionActive(false);
      }
    } catch (e) {
      // keep defaults
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled || editingPreference) return;
        setMapCenter([position.coords.latitude, position.coords.longitude]);
        setMapZoom(12);
      },
      () => {},
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      }
    );

    return () => {
      cancelled = true;
    };
  }, [editingPreference]);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const url = `${apiUrl('/geocode/reverse')}?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`;
      const response = await fetchWithTimeout(
        url,
        {
          headers: {
            Accept: 'application/json',
          },
        },
        8000
      );

      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
    } catch (error) {
      console.error('Error during reverse geocoding:', error);
    }

    return null;
  }, []);

  const resolveSelectionMetadata = useCallback(async (bounds) => {
    if (!bounds) {
      setSelectionLabel('');
      setSelectionMeta(null);
      return;
    }

    const center = computeBoundsCenter(bounds);
    setSelectionCenter(center);
    setIsResolvingLocation(true);

    try {
      const geocode = await reverseGeocode(center.lat, center.lng);
      const label = geocode?.label || formatFallbackLabel(t, center);

      setSelectionLabel(label);
      setSelectionMeta({
        label,
        city: geocode?.township ?? geocode?.city ?? null,
        region: geocode?.district ?? geocode?.province ?? null,
        country: geocode?.country ?? null,
      });
    } catch (error) {
      console.error('Failed to resolve selection metadata:', error);
      const fallback = formatFallbackLabel(t, center);
      setSelectionLabel(fallback);
      setSelectionMeta({
        label: fallback,
        city: null,
        region: null,
        country: null,
      });
    } finally {
      setIsResolvingLocation(false);
    }
  }, [reverseGeocode, t]);

  const handleSelectionChange = useCallback((bounds) => {
    if (!bounds) {
      setSelectedBounds(null);
      setSelectionCenter(null);
      setSelectionLabel('');
      setSelectionMeta(null);
      return;
    }

    setSelectedBounds(bounds);
    const center = computeBoundsCenter(bounds);
    setSelectionCenter(center);
    if (center) {
      setMapCenter([center.lat, center.lng]);
      setMapZoom(12);
    }
    resolveSelectionMetadata(bounds);
  }, [resolveSelectionMetadata]);

  const handleClearSelection = useCallback(() => {
    setSelectedBounds(null);
    setSelectionCenter(null);
    setSelectionLabel('');
    setSelectionMeta(null);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBounds) {
      toast.error(t('preferencesPage.drawAreaBeforeSave'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingPreference 
        ? apiUrl(`/area-preferences/${editingPreference.id}`)
        : apiUrl('/area-preferences');
      
      const method = editingPreference ? 'PUT' : 'POST';
      const [southWest, northEast] = selectedBounds;
      const center = selectionCenter ?? computeBoundsCenter(selectedBounds);

    const payload = {
        ...formData,
      notify_on_new_properties: hasSubscription ? formData.notify_on_new_properties : true,
      notify_on_price_changes: hasSubscription ? formData.notify_on_price_changes : false,
        region: selectionMeta?.region ?? selectionLabel ?? 'Selected Area',
        city: selectionMeta?.city ?? null,
        country: selectionMeta?.country ?? null,
        min_price: formData.min_price ? parseFloat(formData.min_price) : null,
        max_price: formData.max_price ? parseFloat(formData.max_price) : null,
        property_type: formData.property_type?.trim() || null,
        property_category: formData.property_category?.trim() || null,
        selection_label: selectionLabel,
        selection: {
          south_west: {
            lat: southWest[0],
            lng: southWest[1],
          },
          north_east: {
            lat: northEast[0],
            lng: northEast[1],
          },
          center,
          label: selectionLabel,
          city: selectionMeta?.city ?? null,
          region: selectionMeta?.region ?? null,
          country: selectionMeta?.country ?? null,
        },
      };

      const response = await fetchWithTimeout(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }, 10000); // 10 second timeout

      const data = await response.json();
      
      if (data.success) {
        toast.success(editingPreference ? t('preferencesPage.updatedSuccess') : t('preferencesPage.createdSuccess'));
        fetchPreferences();
        resetForm();
      } else {
        toast.error(data.message || t('preferencesPage.failedToSave'));
      }
    } catch (error) {
      console.error('Error saving preference:', error);
      if (error.message === 'Request timeout') {
        toast.error(t('common.requestTimeout'));
      } else {
        toast.error(t('preferencesPage.failedToSave'));
      }
    }
  };

  const handleDelete = async (preferenceId) => {
    if (!window.confirm(t('preferencesPage.confirmDelete'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetchWithTimeout(apiUrl(`/area-preferences/${preferenceId}`), {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }, 10000); // 10 second timeout

      const data = await response.json();
      
      if (data.success) {
        toast.success(t('preferencesPage.deletedSuccess'));
        fetchPreferences();
      } else {
        toast.error(data.message || t('preferencesPage.failedToDelete'));
      }
    } catch (error) {
      console.error('Error deleting preference:', error);
      if (error.message === 'Request timeout') {
        toast.error(t('common.requestTimeout'));
      } else {
        toast.error(t('preferencesPage.failedToDelete'));
      }
    }
  };

  const handleEdit = (preference) => {
    setEditingPreference(preference);
    setFormData({
      min_price: preference.min_price ?? '',
      max_price: preference.max_price ?? '',
      property_type: preference.property_type ?? '',
      property_category: preference.property_category ?? '',
      notify_on_new_properties: preference.notify_on_new_properties,
      notify_on_price_changes: preference.notify_on_price_changes,
    });

    const bounds = preference.selection_bounds
      ?? (preference.sw_lat !== null && preference.sw_lng !== null && preference.ne_lat !== null && preference.ne_lng !== null
        ? [
            [parseFloat(preference.sw_lat), parseFloat(preference.sw_lng)],
            [parseFloat(preference.ne_lat), parseFloat(preference.ne_lng)],
          ]
        : null);

    setSelectedBounds(bounds);

    const label = preference.selection_label || preference.region || 'Saved area';
    setSelectionLabel(label);
    setSelectionMeta({
      label,
      city: preference.city,
      region: preference.region,
      country: preference.country,
    });

    if (bounds) {
      const center = computeBoundsCenter(bounds);
      setSelectionCenter(center);
      if (center) {
        setMapCenter([center.lat, center.lng]);
        setMapZoom(12);
        setMapKey((prev) => prev + 1);
      }
    } else {
      setSelectionCenter(null);
      toast(t('preferencesPage.drawNewAreaToUpdate'), { icon: 'ℹ️' });
    }

    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setSelectedBounds(null);
    setSelectionCenter(null);
    setSelectionLabel('');
    setSelectionMeta(null);
    setMapCenter(INITIAL_MAP_CENTER);
    setMapZoom(11);
    setMapKey((prev) => prev + 1);
    setShowAddForm(false);
    setEditingPreference(null);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const currentSelectionSummary = useMemo(
    () => formatBoundsSummary(selectedBounds),
    [selectedBounds]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('preferencesPage.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('preferencesPage.title')}</h1>
              <p className="text-gray-600 mt-1">{t('preferencesPage.subtitle')}</p>
            </div>
            <div className="flex w-full flex-wrap gap-3 sm:w-auto sm:flex-nowrap">
              <button
                onClick={() => navigate('/preview')}
                className="w-full sm:w-auto bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('preferencesPage.browseProperties')}
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('preferencesPage.addPreference')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 gap-8">
          {/* Email & app language */}
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('preferencesPage.emailAppLanguage')}
            </label>
            <p className="text-xs text-gray-500 mb-3">{t('preferencesPage.emailAppLanguageHelp')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg" aria-hidden>🌐</span>
              <select
                value={currentLanguage}
                onChange={(e) => changeLanguage(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[180px]"
                aria-label={t('preferencesPage.emailAppLanguage')}
              >
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Main Content */}
          <div>
            {!hasSubscription && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                <h2 className="text-lg font-semibold text-yellow-900 mb-2">{t('preferencesPage.basicAlertsTitle')}</h2>
                <p className="text-yellow-800 text-sm mb-4">
                  {t('preferencesPage.basicAlertsBody')}
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('preferencesPage.goToSubscription')}
                </button>
              </div>
            )}
            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {editingPreference ? t('preferencesPage.editPreference') : t('preferencesPage.addNewPreference')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('preferencesPage.notificationArea')} *
                        </label>
                        {!needsMapSelectionActivation ? (
                          <p className="text-xs text-gray-500 mt-1">
                            {t('preferencesPage.notificationAreaHelp')}
                          </p>
                        ) : null}
                      </div>
                      {selectedBounds && (
                        <button
                          type="button"
                          onClick={handleClearSelection}
                          className="text-sm text-gray-600 hover:text-gray-800 underline"
                        >
                          {t('preferencesPage.clearSelection')}
                        </button>
                      )}
                    </div>

                    {needsMapSelectionActivation && (
                      <div className="flex flex-col gap-2 rounded-lg border border-blue-100 bg-blue-50/80 p-3">
                        {!mapSelectionActive ? (
                          <>
                            <p className="text-xs text-gray-700">{t('preferencesPage.touchSelectionIntro')}</p>
                            <button
                              type="button"
                              onClick={() => setMapSelectionActive(true)}
                              className="w-full sm:w-auto px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                            >
                              {t('searchPage.activateSelectionMode')}
                            </button>
                          </>
                        ) : (
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <p className="text-xs text-gray-700 flex-1">{t('preferencesPage.drawSelectionDragHint')}</p>
                            <button
                              type="button"
                              onClick={() => setMapSelectionActive(false)}
                              className="text-sm text-blue-800 hover:text-blue-950 underline whitespace-nowrap self-start"
                            >
                              {t('preferencesPage.mapPanOnly')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div
                      className={`h-72 border border-gray-300 rounded-lg overflow-hidden shadow-sm ${
                        needsMapSelectionActivation && mapSelectionActive ? 'touch-none' : ''
                      }`}
                    >
                      <MapContainer
                        key={mapKey}
                        center={mapCenter}
                        zoom={mapZoom}
                        minZoom={6}
                        maxZoom={15}
                        scrollWheelZoom={!needsMapSelectionActivation || !mapSelectionActive}
                        style={{ height: '100%', width: '100%' }}
                        className="w-full h-full"
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {needsMapSelectionActivation && mapSelectionActive && (
                          <MapSelectionGestureLock active />
                        )}
                        {(!needsMapSelectionActivation || mapSelectionActive) && (
                          <SelectionDrawer
                            selectionBounds={selectedBounds}
                            onSelectionChange={handleSelectionChange}
                            holdDuration={400}
                            bypassTouchHold={needsMapSelectionActivation && mapSelectionActive}
                            suppressMapGestureRestore={needsMapSelectionActivation && mapSelectionActive}
                            rectangleOptions={{
                              color: '#2563eb',
                              weight: 1.5,
                              fillOpacity: 0.15,
                            }}
                          />
                        )}
                      </MapContainer>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-gray-900">
                          {selectionLabel || t('preferencesPage.noAreaSelected')}
                          {isResolvingLocation && (
                            <span className="ml-2 text-xs text-blue-600">{t('preferencesPage.resolvingLocation')}</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {currentSelectionSummary}
                        </span>
                      </div>
                    </div>
                  </div>

                  {hasSubscription ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('preferencesPage.minPrice')}
                          </label>
                          <input
                            type="number"
                            name="min_price"
                            value={formData.min_price}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="100000"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('preferencesPage.maxPrice')}
                          </label>
                          <input
                            type="number"
                            name="max_price"
                            value={formData.max_price}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="500000"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('preferencesPage.propertyType')}
                          </label>
                          <select
                            name="property_type"
                            value={formData.property_type}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">{t('preferencesPage.propertyTypeBoth')}</option>
                            <option value="sale">{t('preferencesPage.saleOnly')}</option>
                            <option value="rent">{t('preferencesPage.rentOnly')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('preferencesPage.propertyCategory')}
                          </label>
                          <select
                            name="property_category"
                            value={formData.property_category}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">{t('preferencesPage.propertyCategoryAll')}</option>
                            <option value="land_lot">{t('propertyDetailPage.categories.land_lot')}</option>
                            <option value="house">{t('propertyDetailPage.categories.house')}</option>
                            <option value="apartment">{t('propertyDetailPage.categories.apartment')}</option>
                            <option value="commercial">{t('propertyDetailPage.categories.commercial')}</option>
                            <option value="other">{t('propertyDetailPage.categories.other')}</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="notify_on_new_properties"
                            checked={formData.notify_on_new_properties}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('preferencesPage.notifyNew')}</span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="notify_on_price_changes"
                            checked={formData.notify_on_price_changes}
                            onChange={handleInputChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{t('preferencesPage.notifyPrice')}</span>
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center space-x-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span>{t('preferencesPage.notifyNew')}</span>
                    </div>
                  )}

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingPreference ? t('preferencesPage.updatePreference') : t('preferencesPage.addPreference')}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Preferences List */}
            <div className="space-y-6">
              {preferences.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                  <div className="text-gray-400 text-6xl mb-4">📍</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('preferencesPage.noPreferencesTitle')}</h3>
                  <p className="text-gray-600 mb-4">{t('preferencesPage.noPreferencesBody')}</p>
                  <button
                    onClick={() => {
                      resetForm();
                      setShowAddForm(true);
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('preferencesPage.addFirstPreference')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('preferencesPage.yourPreferencesTitle')}</h2>
                    <p className="text-gray-600 mb-4">
                      {t('preferencesPage.yourPreferencesBody')}
                    </p>
                    {preferences.some((p) => p.selection_bounds && p.selection_bounds.length >= 2) && (
                      <div className="mt-4 rounded-lg overflow-hidden border border-gray-200" style={{ height: '320px' }}>
                        <MapContainer
                          center={INITIAL_MAP_CENTER}
                          zoom={8}
                          minZoom={4}
                          maxZoom={15}
                          scrollWheelZoom
                          style={{ height: '100%', width: '100%' }}
                          className="w-full h-full"
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          {preferences
                            .filter((p) => p.selection_bounds && Array.isArray(p.selection_bounds) && p.selection_bounds.length >= 2)
                            .map((p) => (
                              <Rectangle
                                key={p.id}
                                bounds={[p.selection_bounds[0], p.selection_bounds[1]]}
                                pathOptions={{
                                  color: '#2563eb',
                                  fillColor: '#3b82f6',
                                  fillOpacity: 0.2,
                                  weight: 2,
                                }}
                              >
                                <Popup>
                                  <div className="text-sm">
                                    <div className="font-medium">{p.selection_label || p.region || t('preferencesPage.selectedArea')}</div>
                                    {p.property_type && <div className="text-gray-600">{t(`preferencesPage.type.${p.property_type}`)}</div>}
                                  </div>
                                </Popup>
                              </Rectangle>
                            ))}
                          <FitBoundsToPreferenceAreas preferences={preferences} />
                        </MapContainer>
                      </div>
                    )}
                  </div>

                  {preferences.map((preference) => (
                    <div key={preference.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 overflow-hidden">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 break-words">
                              {preference.selection_label || preference.region || 'Saved area'}
                            </h3>
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium capitalize shrink-0">
                              {preference.property_type ? t(`preferencesPage.type.${preference.property_type}`) : t('preferencesPage.type.both')}
                            </span>
                            {preference.property_category && (
                              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium shrink-0">
                                {t(`propertyDetailPage.categories.${preference.property_category}`)}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-gray-600 mb-3 text-sm">
                            {preference.selection_bounds
                              ? formatBoundsSummary(preference.selection_bounds)
                              : (preference.city || preference.country
                                  ? [preference.city, preference.country].filter(Boolean).join(', ')
                                  : t('preferencesPage.areaDrawnOnMap'))}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                            <div>
                              <span className="text-sm font-medium text-gray-700">{t('preferencesPage.priceRangeLabel')}</span>
                              <div className="text-green-600 font-semibold">
                                {preference.min_price && preference.max_price 
                                  ? `${formatPrice(preference.min_price)} - ${formatPrice(preference.max_price)}`
                                  : preference.min_price 
                                    ? t('preferencesPage.fromPrice', { price: formatPrice(preference.min_price) })
                                    : preference.max_price
                                      ? t('preferencesPage.upToPrice', { price: formatPrice(preference.max_price) })
                                      : t('preferencesPage.anyPrice')}
                              </div>
                            </div>
                            
                            <div>
                              <span className="text-sm font-medium text-gray-700">{t('preferencesPage.notificationsLabel')}</span>
                              <div className="text-sm text-gray-600">
                                {preference.notify_on_new_properties && `📧 ${t('preferencesPage.newProperties')}`}
                                {preference.notify_on_new_properties && preference.notify_on_price_changes && ', '}
                                {preference.notify_on_price_changes && `💰 ${t('preferencesPage.priceChanges')}`}
                                {!preference.notify_on_new_properties && !preference.notify_on_price_changes && t('preferencesPage.none')}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex w-full sm:w-auto flex-row flex-wrap gap-2 sm:justify-end sm:shrink-0 sm:ml-4">
                          <button
                            type="button"
                            onClick={() => handleEdit(preference)}
                            className={`flex-1 min-w-[7rem] sm:flex-none bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              hasSubscription ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(preference.id)}
                            className={`flex-1 min-w-[7rem] sm:flex-none bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              hasSubscription ? 'hover:bg-red-700' : 'opacity-50 cursor-not-allowed'
                            }`}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Help Section */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 {t('preferencesPage.howItWorksTitle')}</h3>
              <ul className="text-blue-700 space-y-1 text-sm">
                <li>• {t('preferencesPage.howItWorks1')}</li>
                <li>• {t('preferencesPage.howItWorks2')}</li>
                <li>• {t('preferencesPage.howItWorks3')}</li>
                <li>• {t('preferencesPage.howItWorks4')}</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AreaPreferencesPage;

